import { Directive, Input, TemplateRef, ViewContainerRef } from "@angular/core";
import { UsersService } from "../services/users.service";

/**
 * Structural directive that conditionally includes a template based on the current user's role or skill.
 *
 * Usage:
 * ```html
 * <div *isRole="'admin'">This content is visible only to users with tipo 'admin' or skill 'admin'.</div>
 * <div *isRole="['admin', 'editor']">Visible to users with tipo or skill 'admin' or 'editor'.</div>
 * <div *isRole="{ tipo: 'manager', skill: 'lead' }">Visible only to users with tipo 'manager' AND skill 'lead'.</div>
 * <div *isRole="{ tipo: ['admin', 'manager'], skill: ['lead', 'expert'] }">Visible only to users with tipo in ['admin', 'manager'] AND skill in ['lead', 'expert'].</div>
 * ```
 *
 * The directive checks the current user's tipo and skill (from `UsersService`) and renders the template
 * if the user matches the specified criteria.
 *
 * @example
 * <button *isRole="'manager'">Manager actions</button>
 *
 * @example
 * <section *isRole="{ tipo: 'analisis', skill: ['manager'] }">Admin superuser section</section>
 */
@Directive({
  selector: "[isRole]",
})
export class IsRoleDirective {
  private currentRole: string | string[] | { tipo?: string | string[]; skill?: string | string[] };

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private usersService: UsersService
  ) {}

  @Input()
  set isRole(role: string | string[] | { tipo?: string | string[]; skill?: string | string[] }) {
    this.currentRole = role;
    this.updateView();
  }

  private updateView() {
    const user = this.usersService.getCurrentUser();

    let hasRole = false;

    if (typeof this.currentRole === "string" || Array.isArray(this.currentRole)) {
      // Search in tipo and skill (OR)
      const roles = Array.isArray(this.currentRole) ? this.currentRole : [this.currentRole];
      hasRole = roles.includes(user.tipo) || (user.skill && roles.includes(user.skill));
    } else if (typeof this.currentRole === "object") {
      // Search by tipo AND skill
      let tipoOk = true;
      let skillOk = true;

      if (this.currentRole.tipo) {
        const tipos = Array.isArray(this.currentRole.tipo) ? this.currentRole.tipo : [this.currentRole.tipo];
        tipoOk = tipos.includes(user.tipo);
      }
      if (this.currentRole.skill) {
        const skills = Array.isArray(this.currentRole.skill) ? this.currentRole.skill : [this.currentRole.skill];
        skillOk = skills.includes(user.skill);
      }
      hasRole = tipoOk && skillOk;
    }

    this.viewContainer.clear();
    if (hasRole) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    }
  }
}
